import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, Check } from 'lucide-react';

/**
 * ShareInlineButton — Iter 172 (Feb 2026)
 *
 * Tiny single-button share control for embedding inside list cards
 * (business offers, business events, etc.). Uses Web Share API where
 * available, falls back to clipboard copy. Differs from <ShareButtons>
 * (the wider bar) by being unobtrusive enough to live next to other
 * inline controls without dominating the row.
 *
 * Props:
 *   url   – absolute or relative URL to share (required)
 *   title – short, user-facing title for the share sheet (required)
 *   text  – optional descriptive text body for the share sheet
 *   label – optional visible label (default: hidden / icon-only on small)
 *   testId – data-testid suffix (e.g. `offer-${id}` → `share-inline-offer-${id}`)
 */
const ShareInlineButton = ({ url, title, text, label, testId, className = '' }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!url || typeof url !== 'string' || !url.trim()) return null;

  const handleClick = async (e) => {
    e.stopPropagation();
    const payload = { title: title || 'Find-2-Day', text: text || title || '', url };
    if (navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        // fall through to clipboard fallback
      }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const visibleLabel = label || t('share', 'Share');

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium
                  bg-white/80 hover:bg-white text-gray-700 border border-gray-200
                  hover:border-gray-300 transition-colors ${className}`}
      data-testid={testId ? `share-inline-${testId}` : 'share-inline'}
      aria-label={t('shareAria', 'Share') + ': ' + (title || '')}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Share2 className="h-3.5 w-3.5" />}
      <span>{copied ? t('linkCopied', 'Copied!') : visibleLabel}</span>
    </button>
  );
};

export default ShareInlineButton;
